import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { Button, Heading, Icon, Modal, Paragraph, Text, TextButton, Upsell } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconDollarSignCircleFilled } from '@zillow/constellation-icons';

import { isSigningCompleteModalOpenState } from '../../../../atoms';
import { Lease } from '../../../models';
import Property from '../../../models/Property';
import LeaseExtension from '../../../models/LeaseExtension';
import { useCheckActivePaymentsQuery, useLeaseExtensionQuery, useLeaseQuery } from '../../../utils/queries';
import { useLeasingUrlParams } from '../../../hooks';

export function SigningCompleteModal() {
  const history = useHistory();
  const { renewalId, propertyId } = useLeasingUrlParams();

  const leaseExtensionQuery = useLeaseExtensionQuery(renewalId);
  const leaseExtension = new LeaseExtension(leaseExtensionQuery?.data);

  const leaseQuery = useLeaseQuery(leaseExtension.leaseId, { enabled: !!leaseExtension.leaseId });
  const lease = leaseQuery.data;

  const checkActivePaymentsQuery = useCheckActivePaymentsQuery(leaseExtension.leaseId, {
    enabled: !!leaseExtension.leaseId,
  });
  const hasActivePayments = checkActivePaymentsQuery?.data?.activePayments;

  const [isSigningCompleteModalOpen, setIsSigningCompleteModalOpen] = useRecoilState(isSigningCompleteModalOpenState);

  function onGoToYourDashboardClick(): void {
    setIsSigningCompleteModalOpen(false);
    history.push(Lease.getDashboardPath());
  }

  function onSigningCompleteModalClose(): void {
    setIsSigningCompleteModalOpen(false);
  }

  function onGoToPayments(): void {
    if (lease?.encodedId) {
      window.location.href = Property.getPaymentsDashboardPath(propertyId, lease.encodedId);
    }
  }

  return (
    <Modal
      size={{ base: 'fullScreen', md: 'xs' }}
      open={isSigningCompleteModalOpen}
      onOpenChange={(open) => {
        if (!open) {
          onSigningCompleteModalClose();
        }
      }}
      header={<Heading level={1}>Signing complete</Heading>}
      body={
        <>
          <Icon size="lg" role="img" css={{ marginBottom: 'tight', color: 'icon.feedback.success.strong' }}>
            <IconCheckmarkCircleFilled />
          </Icon>
          <Heading
            level={2}
            css={{
              textStyle:
                'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tight',
            }}
          >
            Your lease renewal’s finalized
          </Heading>
          <Paragraph>
            All parties have signed the lease renewal agreement, and your lease will renew{' '}
            <strong>{leaseExtension.getFriendlyStartDate()}</strong>. You can view the agreement and the current lease
            anytime on your dashboard.
          </Paragraph>
          {hasActivePayments && lease?.encodedId && (
            <Upsell
              css={{ marginTop: 'tight' }}
              header={<Heading level={3}>Renew Zillow Payments</Heading>}
              body={<Text>Continue collecting rent online.</Text>}
              media={
                <Icon size="lg" role="img">
                  <IconDollarSignCircleFilled />
                </Icon>
              }
              action={
                <TextButton tone="brand" onClick={onGoToPayments}>
                  Go to payments
                </TextButton>
              }
              tone="soft"
              elevated={false}
              closeable={false}
            />
          )}
        </>
      }
      footer={
        <Button onClick={onGoToYourDashboardClick} tone="brand" emphasis="filled">
          Go to your dashboard
        </Button>
      }
    />
  );
}
